import { bookingAttemptAfterFailure, bookingFailureKind } from "./attempt.js";

export interface BulkBookingOutcome {
  /** The slot's `starts_at`, which identifies it throughout the picker. */
  startsAt: string;
  status: "booked" | "rejected" | "uncertain";
  /** The attempt key this slot ended on — reused as-is when uncertain. */
  key: string;
  message?: string;
}

export interface BulkBookingReport {
  outcomes: BulkBookingOutcome[];
  booked: string[];
  /** Attempt keys to carry into a retry, by `starts_at`. */
  keys: Record<string, string>;
}

/**
 * Book several chosen times, one after another.
 *
 * Sequential on purpose. Each booking spends a credit, and credits are counted
 * upstream: five requests in flight together can each be told there is a credit
 * available and only some of them be right. Going in order means every answer
 * is given against the state the previous one left behind.
 *
 * **It does not stop at the first failure.** A family who picked four times and
 * lost the second to somebody else still wants the other three, and re-choosing
 * them is exactly the reload this exists to remove. The report says what
 * happened to each.
 *
 * Keys belong to the logical attempt for a slot, not to one request, which is
 * what stops a retry spending a second credit on a time already booked. An
 * uncertain outcome keeps its key; a definite rejection gets a fresh one. That
 * rule lives in `attempt.ts` and is not restated here.
 */
export async function bookEachSlot(
  slots: readonly { startsAt: string }[],
  keys: Readonly<Record<string, string>>,
  book: (startsAt: string, key: string) => Promise<void>,
  createKey: () => string,
  describe: (cause: unknown) => string,
): Promise<BulkBookingReport> {
  const outcomes: BulkBookingOutcome[] = [];
  const nextKeys: Record<string, string> = {};

  for (const slot of slots) {
    const key = keys[slot.startsAt] ?? createKey();
    try {
      await book(slot.startsAt, key);
      outcomes.push({ startsAt: slot.startsAt, status: "booked", key });
    } catch (cause) {
      const attempt = bookingAttemptAfterFailure(key, cause, createKey);
      outcomes.push({
        startsAt: slot.startsAt,
        status: bookingFailureKind(cause),
        key: attempt.key,
        message: describe(cause),
      });
      nextKeys[slot.startsAt] = attempt.key;
    }
  }

  return {
    outcomes,
    booked: outcomes.filter((outcome) => outcome.status === "booked").map((o) => o.startsAt),
    keys: nextKeys,
  };
}

/**
 * What to tell the family after a run.
 *
 * It says how many of how many, always, and never stops at the good news. A
 * family who asked for four lessons and reads "Booked 3 lessons" has to count
 * to discover the fourth did not happen — and the one they are most likely to
 * turn up for is the one nobody booked.
 *
 * The shortfall is named, not implied, and the two kinds of failure are kept
 * apart. A refusal is proof nothing happened. A timeout or a 502 is not: it may
 * well have been booked, and saying "not booked" there invites a second attempt
 * that spends a second credit on a lesson the family already has.
 */
export function bulkBookingSummary(report: BulkBookingReport): string | null {
  const total = report.outcomes.length;
  if (total === 0) return null;
  const booked = report.booked.length;
  const uncertain = report.outcomes.filter((outcome) => outcome.status === "uncertain").length;
  const rejected = total - booked - uncertain;

  const parts: string[] = [];
  if (booked > 0) parts.push(`Booked ${booked} of ${total} lesson${total === 1 ? "" : "s"}.`);
  if (rejected > 0) {
    parts.push(
      `${rejected === 1 ? "One time was" : `${rejected} times were`} not booked — ${
        rejected === 1 ? "it is" : "they are"
      } listed below and no credit was used.`,
    );
  }
  if (uncertain > 0) {
    parts.push(
      `${uncertain === 1 ? "One time" : `${uncertain} times`} could not be confirmed either way. Check your lessons before trying ${uncertain === 1 ? "it" : "them"} again.`,
    );
  }
  return parts.join(" ");
}

export interface SelectableSlot {
  startsAt: string;
  /** Credits FastAPI says are eligible for this slot, which is the real limit. */
  eligibleCredits: number;
}

/**
 * How many of these times the family can actually pay for.
 *
 * The **smallest** eligible count among them, not the largest and not a sum. A
 * credit is eligible for a slot or it is not — a block can be in date and still
 * locked to another tutor — so a set of times can only be booked as far as the
 * least-covered one allows. Taking the maximum would offer a fifth lesson on the
 * strength of a credit that only the first four times can spend.
 */
export function creditCapacity(slots: readonly SelectableSlot[]): number {
  if (slots.length === 0) return 0;
  return Math.min(...slots.map((slot) => slot.eligibleCredits));
}

/**
 * Whether one more time can be added to the selection.
 *
 * Asked with the candidate included, because adding it can lower the capacity:
 * three times covered by three credits plus a fourth covered by one is a
 * selection of four against a capacity of one, and the answer has to be no.
 *
 * This is a courtesy, not the enforcement. FastAPI decides at booking time and
 * refuses with `credit_unavailable`, which is handled either way — but a family
 * should not be able to choose a fourth lesson they cannot have and only find
 * out after the first three are booked.
 */
export function canAddSlot(
  selected: readonly SelectableSlot[],
  candidate: SelectableSlot,
): boolean {
  if (selected.some((slot) => slot.startsAt === candidate.startsAt)) return false;
  return selected.length + 1 <= creditCapacity([...selected, candidate]);
}

/** What to say once no more can be chosen. Null while there is room. */
export function selectionLimitMessage(selected: readonly SelectableSlot[]): string | null {
  const capacity = creditCapacity(selected);
  if (selected.length === 0 || selected.length < capacity) return null;
  return capacity === 1
    ? "That is the only credit you have for this lesson. Book it, or unpick it to choose a different time."
    : `That is all ${capacity} of your credits for this lesson. Book these, or unpick one to choose a different time.`;
}
